import { useCallback } from "react";
import RewardManager from "hpay/contracts/RewardManagerV2.json";

import { useContract, useContractWithProvider } from "./contracts/contracts";

export const useRewardManager = (provider = window.web3) => {
    const rewardManagerContract = useContractWithProvider(RewardManager.address, RewardManager.abi, provider);
    const fetchMinRewardsBalance = useCallback(async () => {
        if (!rewardManagerContract) {
            return;
        }

        const result = await rewardManagerContract.methods.minRewardBalance().call();
        return (result / 1e18);
    }, [rewardManagerContract]);

    const claim = useCallback(async (account) => {
        if (!rewardManagerContract) {
            return;
        }

        const gasPrice = await window.web3.eth.getGasPrice();
        let args = {
            from: account,
            gasPrice: gasPrice,
            value: 0
        };
        await rewardManagerContract.methods.claimReward(0).estimateGas(args);
        return rewardManagerContract.methods.claimReward(0).send(args);
    }, [rewardManagerContract]);

    const stake = useCallback(async (account, amount) => {
        if (!rewardManagerContract) {
            return;
        }

        const gasPrice = await window.web3.eth.getGasPrice();
        let args = {
            from: account,
            gasPrice: gasPrice,
            value: 0
        };

        const wei = window.web3.utils.toWei(amount + '');
        await rewardManagerContract.methods.deposit(wei).estimateGas(args);
        return rewardManagerContract.methods.deposit(wei).send(args);
    }, [rewardManagerContract]);

    const unstake = useCallback(async (account, amount) => {
        if (!rewardManagerContract) {
            return;
        }

        const gasPrice = await window.web3.eth.getGasPrice();
        let args = {
            from: account,
            gasPrice: gasPrice,
            value: 0
        };

        const wei = window.web3.utils.toWei(amount + '');

        await rewardManagerContract.methods.withdraw(wei).estimateGas(args);
        return rewardManagerContract.methods.withdraw(wei).send(args);
    }, [rewardManagerContract]);

    return { claim, stake, unstake, fetchMinRewardsBalance };
};


export const useMinRewardBalance = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);
    const fetchMinRewardsBalance = useCallback(async () => {
        if (!rewardManagerContract) {
            return;
        }

        const result = await rewardManagerContract.methods.minRewardBalance().call();
        return (result / 1e18);
    }, [rewardManagerContract]);

    return fetchMinRewardsBalance;
};

export const useIsRewardEligible = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);
    const fetchData = useCallback(async (address) => {
        if (!rewardManagerContract) {
            return false;
        }
        return await rewardManagerContract.methods.rewardEligible(address).call();
    }, [rewardManagerContract]);

    return fetchData;
};

export const useUserPendingRewards = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);

    const fetchPendingRewards = useCallback(async (address) => {
        if (!rewardManagerContract) {
            return 0;
        }

        const profit = await rewardManagerContract.methods.pendingReward(address).call();
        return profit / 1e18;
    }, [rewardManagerContract]);

    return fetchPendingRewards;
};

export const useInvestorPoolAmount = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);

    const fetchPoolAmount = useCallback(async (address) => {
        if (!rewardManagerContract) {
            return 0;
        }

        const { amount } = await rewardManagerContract.methods.investorInfo(address).call();
        return amount / 1e18;
    }, [rewardManagerContract]);

    return fetchPoolAmount;
};

export const useInvestorStakeAmount = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);

    const fetchPoolAmount = useCallback(async (address) => {
        if (!rewardManagerContract) {
            return 0;
        }

        const amount = await rewardManagerContract.methods.balance(address).call();
        return amount / 1e18;
    }, [rewardManagerContract]);

    return fetchPoolAmount;
};

export const useInvestorFee = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);

    const fetchInvestorFee = useCallback(async (address) => {
        if (!rewardManagerContract) {
            return 0;
        }

        const fee = await rewardManagerContract.methods.fee(address).call();
        return fee;
    }, [rewardManagerContract]);

    return fetchInvestorFee;
};

export const useUserPoolShare = () => {
    const rewardManagerContract = useContract(RewardManager.address, RewardManager.abi);

    const fetchPoolShare = useCallback(async (address) => {
        if (!rewardManagerContract) {
            return 0;
        }
        const { amount } = await rewardManagerContract.methods.investorInfo(address).call();
        const totalPoolSize = await rewardManagerContract.methods.totalDeposited().call();
        const poolShore = (amount * 100 / totalPoolSize);
        return poolShore;
    }, [rewardManagerContract]);

    return fetchPoolShare;
};